import { Component, Fragment, createElement, type ErrorInfo, type ReactNode } from "react";

/** Falhar de novo dentro desta janela, logo depois de remontar, é laço: o limite desiste. */
const JANELA_DO_LACO_MS = 2000;

interface LimiteDosAvisosProps {
  children: ReactNode;
  /** Chamado a cada queda, para limpar o que quebrou (o aviso ruim não pode voltar na remontagem). */
  aoFalhar: () => void;
}

interface LimiteDosAvisosEstado {
  /** Troca a cada remontagem: é a `key` que força o React a recriar os filhos do zero. */
  chave: number;
  falhou: boolean;
  desistiu: boolean;
}

/**
 * Limite de erro em volta de cada Toaster (shadcn e sonner). O App não tem ErrorBoundary na
 * raiz: um aviso que lança durante o render (o `detail` do 422 como filho do React, por
 * exemplo) derrubava a árvore inteira. Aqui, no erro: chama `aoFalhar()`, registra com o
 * prefixo "[avisos]" e remonta os filhos. Se falhar de novo em menos de 2 s depois de
 * remontar, desiste e renderiza nada, para não entrar em laço. Nunca derruba o resto do App.
 */
export class LimiteDosAvisos extends Component<LimiteDosAvisosProps, LimiteDosAvisosEstado> {
  state: LimiteDosAvisosEstado = { chave: 0, falhou: false, desistiu: false };

  private remontadoEm: number | null = null;

  static getDerivedStateFromError(): Partial<LimiteDosAvisosEstado> {
    return { falhou: true };
  }

  componentDidCatch(erro: Error, info: ErrorInfo): void {
    console.error(
      "[avisos] o aviso quebrou ao renderizar; limpando os avisos e remontando",
      erro,
      info.componentStack
    );
    try {
      this.props.aoFalhar();
    } catch (erroDaLimpeza) {
      console.error("[avisos] a limpeza dos avisos também falhou", erroDaLimpeza);
    }

    const agora = Date.now();
    if (this.remontadoEm !== null && agora - this.remontadoEm < JANELA_DO_LACO_MS) {
      console.error("[avisos] falhou de novo logo depois de remontar; desistindo dos avisos");
      this.setState({ desistiu: true });
      return;
    }
    this.remontadoEm = agora;
    this.setState(({ chave }) => ({ chave: chave + 1, falhou: false }));
  }

  render(): ReactNode {
    if (this.state.desistiu || this.state.falhou) return null;
    // `createElement`, não `<Fragment key>`: o plugin de dev da Vite
    // (`@dyad-sh/react-vite-component-tagger`) injeta `data-dyad-id` em todo elemento JSX, e o
    // React acusaria "Invalid prop supplied to React.Fragment" no console de TODA página.
    return createElement(Fragment, { key: this.state.chave }, this.props.children);
  }
}
